/**
 *  @flow
 */

import React, { useEffect } from 'react';
import gql from 'graphql-tag';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { map, omit } from 'lodash';
import uuid from 'uuid';
import { Icon } from 'semantic-ui-react';

import { Modal, Button } from '../../../components';
import MasterForm from './form/MasterForm';
import useUserLookup from '../../../lib/useUserLookup';
import { GET_MASTERS_QUERY } from './MastersList';

export const ADD_MASTERS_MUTATION = gql`
    mutation addMaster($input: AddMasterInput!, $lang: String!) {
        addMaster(input: $input) {
            master {
                id
                firstName
                lastName
                phone
                email
                type
                status
                gender
                serviceGroups {
                    id
                    name(lang: $lang)
                    type
                }
            }
        }
    }
`;

const intlMessages = defineMessages({
    phoneExistingsError: {
        id: 'pages.staff.form.phoneExistingsError',
        defaultMessage: 'Master with the same phone number already exists',
    },
    emailExistingsError: {
        id: 'pages.staff.form.emailExistingsError',
        defaultMessage: 'Master with the same email already exists',
    },
    emailPhoneError: {
        id: 'pages.clients.form.ClientForm.emailPhoneError',
        defaultMessage: 'There are two different users with the provided phone and email',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

const DEFAULT_VALUES = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '+371',
    serviceGroups: [],
    status: 'ACTIVE',
    gender: '',
};

function AddMasterDialog({ trigger, open, onClose, intl }) {
    const salon = useSelector(({ user }) => user.get('salon'));
    const searchQuery = useSelector(({ staff }) => staff.get('searchQuery'));
    const currentLanguage = useSelector(
        ({ intl: i }) => i.get('locale') || i.get('defaultLanguage'),
    );
    const { userLookupStatus, userLookupUser, onUserLookup, resetUserLookup } = useUserLookup();

    const methods = useForm({ defaultValues: DEFAULT_VALUES });
    const { handleSubmit, reset, setError, getValues, formState: { isSubmitting, isDirty } } = methods;

    useEffect(() => {
        if (open && userLookupUser) {
            const { email, firstName, lastName, phone, gender } = userLookupUser;
            reset({
                ...getValues(),
                email: email || '',
                firstName: firstName || '',
                lastName: lastName || '',
                phone: phone || '+371',
                gender: gender || '',
            });
        }
    }, [userLookupUser]); // eslint-disable-line react-hooks/exhaustive-deps

    const [addMaster] = useMutation(ADD_MASTERS_MUTATION, {
        update: (store, { data: { addMaster: result } }) => {
            try {
                const data = store.readQuery({
                    query: GET_MASTERS_QUERY,
                    variables: {
                        salon,
                        search: searchQuery || undefined,
                        lang: currentLanguage,
                    },
                });
                const nodes = data.viewer.salon.masters.nodes || [];
                store.writeQuery({
                    query: GET_MASTERS_QUERY,
                    variables: {
                        salon,
                        search: searchQuery || undefined,
                        lang: currentLanguage,
                    },
                    data: {
                        ...data,
                        viewer: {
                            ...data.viewer,
                            salon: {
                                ...data.viewer.salon,
                                masters: {
                                    ...data.viewer.salon.masters,
                                    nodes: [...nodes, result.master],
                                },
                            },
                        },
                    },
                });
            } catch (e) {
                // cache not populated — safe to ignore
            }
        },
    });

    const onModalClose = () => {
        resetUserLookup();
        reset(DEFAULT_VALUES);
        onClose();
    };

    const handleClear = () => {
        resetUserLookup();
        reset(DEFAULT_VALUES);
    };

    const onSubmit = async formData => {
        const phone = (formData.phone || '').replace(/[^+\d]/g, '');
        try {
            await addMaster({
                variables: {
                    input: { salon, ...formData, phone },
                    lang: currentLanguage,
                },
                optimisticResponse: {
                    __typename: 'Mutation',
                    addMaster: {
                        __typename: 'AddMasterPayload',
                        master: {
                            __typename: 'Master',
                            id: uuid.v4(),
                            serviceGroups: map(
                                formData.serviceGroups,
                                serviceGroup => ({
                                    __typename: 'ServiceGroups',
                                    id: serviceGroup,
                                    name: '',
                                    type: '',
                                }),
                            ),
                            email: formData.email || null,
                            phone: phone || null,
                            type: 'LOCAL',
                            ...omit(formData, ['serviceGroups']),
                        },
                    },
                },
            });
            onModalClose();
        } catch (error) {
            const graphQLError = error.graphQLErrors && error.graphQLErrors[0];
            if (graphQLError) {
                if (graphQLError.name === 'AlreadyExists') {
                    if (graphQLError.data?.error === 'PHONE_ALREADY_EXISTS') {
                        setError('phone', { message: intl.formatMessage(intlMessages.phoneExistingsError) });
                        return;
                    } else if (graphQLError.data?.error === 'email') {
                        setError('email', { message: intl.formatMessage(intlMessages.emailExistingsError) });
                        return;
                    }
                } else if (
                    graphQLError.name === 'Forbidden' &&
                    graphQLError.data?.error === 'THERE_ARE_TWO_DIFFERENT_USER_WITH_PROVIDED_PHONE_AND_EMAIL'
                ) {
                    setError('root', { message: intl.formatMessage(intlMessages.emailPhoneError) });
                    return;
                }
                setError('root', { message: graphQLError.message });
                return;
            }
            setError('root', { message: intl.formatMessage(intlMessages.defaultErrorMessage) });
        }
    };

    const readOnly = userLookupStatus === 'EXISTS';

    return (
        <FormProvider {...methods}>
            <Modal trigger={trigger} onClose={onModalClose} open={open}>
                <Modal.Header>
                    <FormattedMessage
                        id="pages.masters.AddMasterDialog.header"
                        defaultMessage="New master"
                    />
                </Modal.Header>
                <Modal.Content>
                    <MasterForm
                        readOnly={readOnly}
                        userLookupStatus={userLookupStatus}
                        userLookupUser={userLookupUser}
                        onUserLookup={onUserLookup}
                        resetUserLookup={resetUserLookup}
                    />
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        disabled={isSubmitting || !isDirty}
                        loading={isSubmitting}
                        primary
                    >
                        <FormattedMessage
                            id="pages.masters.AddMasterDialog.create"
                            defaultMessage="Create"
                        />
                    </Button>
                    <Button onClick={handleClear} disabled={isSubmitting} basic>
                        <FormattedMessage
                            id="pages.masters.AddMasterDialog.clear"
                            defaultMessage="Clear"
                        />
                    </Button>
                    <Button onClick={onModalClose} disabled={isSubmitting} basic>
                        <Icon name="remove" />
                        <FormattedMessage
                            id="pages.masters.AddMasterDialog.cancel"
                            defaultMessage="Cancel"
                        />
                    </Button>
                </Modal.Actions>
            </Modal>
        </FormProvider>
    );
}

export default injectIntl(AddMasterDialog);
